import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';

/** Filters that define the Exceptions workspace list identity (ENG-2796). Page is excluded. */
export type ExceptionsListContextInput = {
  status: string;
  type: string | null | undefined;
  severity: string | null | undefined;
  search: string;
};

/** Empty option value for “All Types” / “All Severities” (not sent to the API). */
export const EXCEPTIONS_FILTER_ALL = '';

export function isExceptionsAllFilter(
  value: string | null | undefined,
): boolean {
  return value == null || value === EXCEPTIONS_FILTER_ALL;
}

/**
 * Stable key for the current list context. Changing this must clear multi-select
 * so bulk UI does not carry across Open/Closed, By Type, search, or top filters.
 */
export function buildExceptionsListContextKey(
  input: ExceptionsListContextInput,
): string {
  return [
    input.status || '',
    isExceptionsAllFilter(input.type) ? '' : (input.type ?? ''),
    isExceptionsAllFilter(input.severity) ? '' : (input.severity ?? ''),
    input.search.trim(),
  ].join('\u001f');
}

/** Default top/sidebar filters after switching Status or By Type screens (ENG-2796 QA). */
export type ExceptionsScreenFilters = {
  typeFilter: string;
  severityFilter: string;
  search: string;
};

export function resetExceptionsStatusScreenFilters(): ExceptionsScreenFilters {
  return {
    typeFilter: EXCEPTIONS_FILTER_ALL,
    severityFilter: EXCEPTIONS_FILTER_ALL,
    search: '',
  };
}

/** Keep only selected rows that still appear in the current page/result set. */
export function pruneSelectedExceptions(
  selected: readonly ShipmentExceptionRow[],
  rows: readonly ShipmentExceptionRow[],
): ShipmentExceptionRow[] {
  if (selected.length === 0) {
    return [];
  }
  const ids = new Set(rows.map((r) => r.id));
  return selected.filter((r) => ids.has(r.id));
}

export type ExceptionWorkspaceAction = 'close' | 'reopen' | 'export';
export type ExceptionWorkspaceActionOutcome =
  | 'success'
  | 'failure'
  | 'cancelled';

/** Empty selection after a successful close/reopen (ENG-2794). */
export function clearSelectedExceptions<T = ShipmentExceptionRow>(): T[] {
  return [];
}

/**
 * ENG-2794: successful close/reopen always clears the whole selection.
 * Do not infer from the current list — All Exceptions keeps rows visible after close.
 * Export and failed/cancelled actions keep the current selection.
 */
export function exceptionSelectionAfterAction<T>(
  action: ExceptionWorkspaceAction,
  outcome: ExceptionWorkspaceActionOutcome,
  selected: readonly T[],
): T[] {
  if (outcome === 'success' && (action === 'close' || action === 'reopen')) {
    return clearSelectedExceptions<T>();
  }
  return [...selected];
}
